import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * Jak długo komunikat wisi na ekranie. Na tyle długo, żeby dało się go przeczytać w trakcie
 * pracy, i na tyle krótko, żeby nie zasłaniał treści przy szybkim klikaniu.
 */
export const TOAST_DURATION_MS = 3000;

/**
 * Błąd wisi dłużej: informacja o sukcesie potwierdza to, czego użytkownik się spodziewał,
 * a komunikat o niepowodzeniu trzeba przeczytać, żeby wiedzieć, co poprawić.
 */
export const TOAST_ERROR_DURATION_MS = 5000;

/** Zwykła informacja czy niepowodzenie — od tego zależy wygląd i czas wyświetlania. */
export type ToastKind = 'info' | 'error';

export interface Toast {
  text: string;
  kind: ToastKind;
}

/**
 * Krótkie komunikaty, które nie przerywają pracy.
 *
 * Serwis odpowiada wyłącznie na pytanie „co teraz wisi na dole ekranu"; renderowaniem,
 * animacją i dostępnością zajmuje się <app-toast>. Dzięki temu obie rzeczy dają się
 * testować osobno, a zmiana wyglądu nie wymaga dotykania logiki.
 *
 * Nie ma kolejki: nowy komunikat zastępuje poprzedni. Operator klikający trzy razy pod rząd
 * chce zobaczyć ostatni wynik, a nie odczekać trzy kolejki.
 *
 * Przyjmuje gotowy tekst, nie klucz — jest prymitywem prezentacji, a nie warstwą tłumaczeń.
 *
 * **Nie należą tu komunikaty wymagające decyzji** — pytania „czy na pewno", błędy, po których
 * użytkownik musi coś wybrać, i wszystko, czego przeoczenie zostawia go w niejasnym stanie.
 * Te zostają na ekranie (ModalService).
 */
@Injectable({ providedIn: 'root' })
export class ToastService {

  /** BehaviorSubject, bo host podpina się w swoim ngOnInit — później niż pierwszy show(). */
  private readonly toast = new BehaviorSubject<Toast>(null);
  private hideTimer: any = null;

  /** Bieżący komunikat albo null, gdy nic nie jest pokazywane. */
  readonly current: Observable<Toast> = this.toast.asObservable();

  /** Zwykła informacja — „Zapisano", „Skopiowano IMEI". */
  show(text: string): void {
    this.present({ text, kind: 'info' }, TOAST_DURATION_MS);
  }

  /**
   * Niepowodzenie, które użytkownik ma przeczytać, ale które nie wymaga od niego decyzji —
   * na przykład „popraw pola formularza", gdy pola i tak są już zaznaczone na czerwono.
   */
  showError(text: string): void {
    this.present({ text, kind: 'error' }, TOAST_ERROR_DURATION_MS);
  }

  /**
   * Kilka powodów naraz — każdy w osobnym wierszu. Rozdzielanie wierszem jest sparowane
   * z `white-space: pre-line` w stylach <app-toast>, więc trzyma się jednego miejsca.
   */
  showErrors(texts: string[]): void {
    this.showError(texts.join('\n'));
  }

  hide(): void {
    this.clearTimer();
    this.toast.next(null);
  }

  private present(toast: Toast, duration: number): void {
    // Skasuj poprzedni timer — inaczej gasiłby nowy komunikat po czasie liczonym
    // od poprzedniego kliknięcia.
    this.clearTimer();
    this.toast.next(toast);
    this.hideTimer = setTimeout(() => this.hide(), duration);
  }

  private clearTimer(): void {
    if (this.hideTimer) {
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }
  }
}
